/**
 * Dubbele crew-regels op een callsheet herkennen.
 *
 * Waarom (21 okt 2026, Kiwa-callsheet): bij "Callsheet aanmaken" schrijft de wizard de crew mét
 * functie en telefoon, en ~3 s later voegt de Airtable-automation
 * `AddStandardPLanningAndCrewToNewCallsheet` dezelfde mensen nog eens toe als kale regel
 * (alleen de Personen-link). Bestond al sinds juni; niet door de crew-matching van #453.
 *
 * Bewust voorzichtig — er mag nooit informatie of een ander mens verdwijnen:
 *  - zelfde persoon = dezelfde Personen-id. Regels zonder link alleen bij exact dezelfde volledige
 *    naam én exact dezelfde gegevens (een letterlijke kopie); een naam bewijst verder niets.
 *    Een regel zonder link wordt nooit aan een gekoppelde regel gehangen (en andersom).
 *  - per persoon blijft de regel met de meeste gegevens; een andere regel valt alleen weg als
 *    hij niets heeft wat de bewaarde regel mist (functie, telefoon, calltijd, opmerking,
 *    volgorde, eigen naamtekst).
 *  - vrije tekst wordt alleen op hoofdletters en witruimte genormaliseerd; alleen bij telefoon
 *    tellen spaties/streepjes niet ("06 42 93 68 46" = "0642936846").
 * Identieke logica in de Node-backend (backend/utils/crewDubbelen.js, main) en de engine
 * (services/callsheet_crew_dubbelen.py, develop).
 */

const EIGEN_VELDEN = ['Functie (handmatig)', 'Telefoon (handmatig)', 'Calltijd', 'Opmerkingen', 'Volgorde'];

const eerste = (v) => (Array.isArray(v) ? v[0] : v);
const leeg = (v) => { const e = eerste(v); return e === undefined || e === null || String(e).replace(/​/g, '').trim() === ''; };
const normTekst = (v) => String(eerste(v) ?? '').replace(/​/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
const normTelefoon = (v) => String(eerste(v) ?? '').replace(/[\s​().-]+/g, '');
const normVeld = (veld, v) => (leeg(v) ? '' : (veld === 'Telefoon (handmatig)' ? normTelefoon(v) : normTekst(v)));
export const normNaam = (s) => normTekst(s);

const persoonId = (r) => {
  const id = eerste(r?.fields?.Personen);
  return id ? String(id) : '';
};
const linkNaam = (r) => eerste(r?.fields?.['Volledige naam (from Personen)']) || eerste(r?.fields?.Naam) || '';
const naamVan = (r) => r?.fields?.Persoon || linkNaam(r) || '';

/** Hoeveel bruikbare gegevens een regel heeft (meer = bewaren). */
export function score(r) {
  const f = r?.fields || {};
  let s = 0;
  if (!leeg(f['Functie (handmatig)'])) s += 4;
  if (!leeg(f['Telefoon (handmatig)'])) s += 4;
  if (!leeg(f.Calltijd)) s += 2;
  if (!leeg(f.Opmerkingen)) s += 2;
  if (!leeg(f.Volgorde)) s += 1;
  if (!leeg(f.Persoon)) s += 1;
  return s;
}

/** Kan `r` weg naast `houd`, zonder dat er informatie verloren gaat? */
function zonderVerlies(r, houd, { exact = false } = {}) {
  const f = r?.fields || {};
  const h = houd?.fields || {};
  if (persoonId(r) !== persoonId(houd)) return false;
  for (const veld of EIGEN_VELDEN) {
    const a = normVeld(veld, f[veld]);
    const b = normVeld(veld, h[veld]);
    if (exact ? a !== b : (a !== '' && a !== b)) return false;
  }
  // Een eigen naamtekst ("Tom (via Saskia)") moet dezelfde naam zijn als die van de bewaarde regel.
  if (!leeg(f.Persoon) && normNaam(f.Persoon) !== normNaam(naamVan(houd))) return false;
  if (exact && normNaam(naamVan(r)) !== normNaam(naamVan(houd))) return false;
  return true;
}

const tijd = (r) => Date.parse(r?.createdTime || '') || Number.MAX_SAFE_INTEGER; // zonder tijd: achteraan

/**
 * @param {object[]} rijen crew-records ({ id, createdTime?, fields })
 * @returns {{ houden: object[], weg: {rij: object, houd: object}[] }}  `houden` in de oorspronkelijke volgorde
 */
export function crewDubbelen(rijen) {
  const lijst = Array.isArray(rijen) ? rijen.filter(Boolean) : [];
  const groepen = new Map();
  lijst.forEach((r, i) => {
    const id = persoonId(r);
    const naam = normNaam(naamVan(r));
    const sleutel = id ? `id:${id}` : (naam ? `naam:${naam}` : `rij:${i}`);
    if (!groepen.has(sleutel)) groepen.set(sleutel, []);
    groepen.get(sleutel).push({ r, i });
  });
  const weg = new Map();
  for (const [sleutel, groep] of groepen) {
    if (groep.length < 2) continue;
    const exact = sleutel.startsWith('naam:');
    const gesorteerd = [...groep].sort((a, b) => (score(b.r) - score(a.r)) || (tijd(a.r) - tijd(b.r)) || (a.i - b.i));
    const houd = gesorteerd[0].r;
    for (const { r, i } of gesorteerd.slice(1)) {
      if (zonderVerlies(r, houd, { exact })) weg.set(i, { rij: r, houd });
    }
  }
  return {
    houden: lijst.filter((_r, i) => !weg.has(i)),
    weg: [...weg.keys()].sort((a, b) => a - b).map((i) => weg.get(i)),
  };
}

/** Alleen de te tonen regels. */
export const ontdubbelCrew = (rijen) => crewDubbelen(rijen).houden;

/**
 * De te tonen regels, elk met `_dubbelen` (record-ids van de verborgen kopieën). Zo kan de editor bij
 * verwijderen of vervangen de kopieën meenemen, anders komt de verborgen regel na herladen terug.
 */
export function crewMetDubbelen(rijen) {
  const { houden, weg } = crewDubbelen(rijen);
  const perHoud = new Map();
  for (const w of weg) {
    if (!perHoud.has(w.houd)) perHoud.set(w.houd, []);
    perHoud.get(w.houd).push(w.rij.id);
  }
  return houden.map((r) => (perHoud.has(r) ? { ...r, _dubbelen: perHoud.get(r) } : r));
}
